import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Product inquiry", message: "" });
  const [s, setS] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const fullAddress = [s?.address_line1, s?.address_line2].filter(Boolean).join(", ");
  const directionsUrl = fullAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`
    : "#";
  const mapSrc = s?.map_embed_url
    ? s.map_embed_url
    : (fullAddress ? `https://maps.google.com/maps?q=${encodeURIComponent(fullAddress)}&z=15&output=embed` : "");


  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "Product inquiry", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16">
      <div>
        <span className="eyebrow">Get in Touch</span>
        <h1 className="font-serif text-5xl text-sand mt-3 leading-[1.05]">We'd love to hear from you.</h1>
        <div className="divider my-6" />
        <p className="text-sand/80 leading-relaxed">
          Have a question about a product, an order, or our process? Send us a note and a team
          member will respond within one business day.
        </p>

        <ul className="mt-10 space-y-5 text-sand/85">
          <li className="flex gap-3"><MapPin size={20} className="text-copper flex-shrink-0 mt-1" /><span>{s?.address_line1}<br/>{s?.address_line2}</span></li>
          <li className="flex gap-3"><Phone size={20} className="text-copper flex-shrink-0 mt-1" /><span>{s?.phone}</span></li>
          <li className="flex gap-3"><Mail size={20} className="text-copper flex-shrink-0 mt-1" /><span>{s?.email}</span></li>
          <li className="flex gap-3">
            <Clock size={20} className="text-copper flex-shrink-0 mt-1" />
            <div>
              {s?.hours?.map((h, i) => (
                <div key={i} className="text-sm"><span className="font-medium">{h.day}:</span> {h.open} – {h.close}</div>
              ))}
            </div>
          </li>
        </ul>

        {fullAddress && (
          <>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-copper text-navy px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-tan transition-colors"
            >
              <Navigation size={16} /> Get Directions
            </a>
            {mapSrc && (
              <div className="mt-8 border border-tan/40 overflow-hidden" data-testid="contact-map">
                <iframe
                  title="Store location map"
                  src={mapSrc}
                  width="100%"
                  height="320"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            )}
          </>
        )}
      </div>

      <form onSubmit={submit} className="bg-white p-8 border border-tan/40 space-y-5" data-testid="contact-form">
        <div>
          <label className="text-xs uppercase tracking-wide text-charcoal/80">Your Name</label>
          <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
            className="mt-2 w-full border border-tan/50 px-4 py-3 text-charcoal focus:border-copper focus:outline-none bg-[#FAFAFA]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-charcoal/80">Email</label>
            <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
              className="mt-2 w-full border border-tan/50 px-4 py-3 text-charcoal focus:border-copper focus:outline-none bg-[#FAFAFA]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-charcoal/80">Phone (optional)</label>
            <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
              className="mt-2 w-full border border-tan/50 px-4 py-3 text-charcoal focus:border-copper focus:outline-none bg-[#FAFAFA]" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-charcoal/80">Subject</label>
          <select data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}
            className="mt-2 w-full border border-tan/50 px-4 py-3 text-charcoal focus:border-copper focus:outline-none bg-[#FAFAFA]">
            <option>Product inquiry</option>
            <option>Wholesale</option>
            <option>Order question</option>
            <option>Press / partnership</option>
            <option>Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-charcoal/80">Message</label>
          <textarea data-testid="contact-message" rows={5} required value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
            className="mt-2 w-full border border-tan/50 px-4 py-3 text-charcoal focus:border-copper focus:outline-none bg-[#FAFAFA]" />
        </div>
        <button data-testid="contact-submit" disabled={submitting} className="bg-navy text-white px-8 py-4 text-base uppercase tracking-wide hover:bg-charcoal transition-colors w-full disabled:opacity-50">
          {submitting ? "Sending…" : "Send Message"}
        </button>
      </form>
    </div>
  );
}
